import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Loader, Textarea, Timeline } from '@any-ui-react/core';
import classNames from 'classnames';

import { EditIcon } from '@anyx/shared-icons';


import { CustomerActivityTimelineNode } from './customer-activity-timeline-item';
import {
  CustomerActivityTimelineProvider,
  OnDeleteNoteActivity,
  OnUpdateNoteActivity,
} from './CustomerActivityTimelineContext';
import { CustomerActivityTimelineError } from './CustomerActivityTimelineError';
import { CustomerActivityTimelineSkeleton } from './CustomerActivityTimelineSkeleton';
import {
  CustomerCreatedActivity,
  CustomerNotedActivity,
  CustomerOptedOutActivity,
  CustomerOptInAcceptedActivity,
  CustomerOptInRequestedActivity,
  CustomerOrderedActivity,
  CustomerProfileUpdatedActivity,
  InfluencerJoinedCampaignActivity,
  InfluencerPublishedPostActivity,
  InfluencerSubmittedPostActivity,
} from './factory';

export type CustomerActivityTimelineClassNames = {
  timeline?: string;
};

export interface CustomerActivityTimelineProps {
  classNames?: CustomerActivityTimelineClassNames;
  /** Callback function when posting a comment */
  onPostComment?: (comment: string) => Promise<boolean>;
  /** Callback function when editing a note */
  onUpdateNoteActivity?: OnUpdateNoteActivity;
  /** Callback function when deleting a note */
  onDeleteNoteActivity?: OnDeleteNoteActivity;
  /** compact mode, e.g. the position of time display will be different */
  compact?: boolean;
  /** Whether there are more items to load when scrolling to the bottom */
  hasMore?: boolean;
  /** Callback function when loading for more */
  onLoadMore?: () => void;
  /** is loading more items */
  isLoadingMore?: boolean;
  /** is empty items */
  isEmpty?: boolean;
  /** Custom empty state text */
  emptyMessage?: string;
  /** Show outer border (default: true) */
  bordered?: boolean;
  children?: React.ReactNode;
}

export const CustomerActivityTimeline = ({
  classNames: slotClassNames,
  onPostComment,
  onLoadMore,
  onUpdateNoteActivity,
  onDeleteNoteActivity,
  compact,
  hasMore,
  isLoadingMore,
  isEmpty,
  emptyMessage,
  bordered = true,
  children,
}: CustomerActivityTimelineProps) => {
  const { t } = useTranslation('anychat');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isPosting, setIsPosting] = useState(false);

  const handlePostComment = async () => {
    if (!textAreaRef.current) return;

    const comment = textAreaRef.current.value;
    setIsPosting(true);
    const success = await onPostComment?.(comment);
    setIsPosting(false);
    if (success) {
      textAreaRef.current.value = '';
    }
  };

  const handleLoadMore = () => {
    onLoadMore?.();
  };

  return (
    <CustomerActivityTimelineProvider
      compact={compact}
      onUpdateNoteActivity={onUpdateNoteActivity}
      onDeleteNoteActivity={onDeleteNoteActivity}
    >
      <div className={classNames('relative flex h-full flex-col', bordered && 'rounded-sm border')}>
        {onPostComment && (
          <div className="card flex gap-2 bg-white p-4">
            <Textarea
              ref={textAreaRef}
              leftSection={<EditIcon width={16} height={16} className="text-gray-6" />}
              rows={1}
              maxRows={3}
              autosize
              className="max-h-20 w-full"
              placeholder={t('anychat.shared.customerActivityTimeline.note.placeholder')}
            />
            <Button onClick={handlePostComment} loading={isPosting}>
              {t('anychat.shared.customerActivityTimeline.note.action')}
            </Button>
          </div>
        )}

        {isEmpty && <CustomerActivityEmptyTimeline emptyMessage={emptyMessage} />}
        {!isEmpty && children && (
          <Timeline
            color="gray"
            lineWidth={2}
            bulletSize={8}
            className={classNames(
              'bg-gray-0 pr-1',
              // When not bordered the parent ChatAsidePanel.Content has pt-4;
              // -mt-4 pulls the timeline flush against the header above it.
              bordered ? 'h-full overflow-auto' : '-mt-4',
              slotClassNames?.timeline
            )}
          >
            <CustomerActivityTimelineNode hideBullet>
              <div className="h-1" />
            </CustomerActivityTimelineNode>
            {children}
            {isLoadingMore ? (
              <div className="flex justify-center">
                <Loader size="sm" />
              </div>
            ) : (
              hasMore && (
                <div className="relative flex pl-px">
                  <div className="absolute -bottom-6 left-0 z-20 flex h-24 w-full items-end justify-center bg-(image:--crm-more-chat-activity) px-6 py-2">
                    <Button variant="default" onClick={handleLoadMore} loading={isLoadingMore}>
                      {t('shared.button.more', {
                        ns: 'shared',
                      })}
                    </Button>
                  </div>
                </div>
              )
            )}
          </Timeline>
        )}
      </div>
    </CustomerActivityTimelineProvider>
  );
};

const CustomerActivityEmptyTimeline = ({ emptyMessage }: { emptyMessage?: string }) => {
  const { t } = useTranslation('shared');

  return (
    <div className="flex justify-center p-6">
      <p className="text-sm">
        {emptyMessage ??
          t('shared.table.empty', {
            element: t('shared.entity.activity', { count: 2 }),
          })}
      </p>
    </div>
  );
};

CustomerActivityTimeline.CustomerCreated = CustomerCreatedActivity;
CustomerActivityTimeline.CustomerOrdered = CustomerOrderedActivity;
CustomerActivityTimeline.ProfileUpdated = CustomerProfileUpdatedActivity;
CustomerActivityTimeline.OptInRequested = CustomerOptInRequestedActivity;
CustomerActivityTimeline.OptInAccepted = CustomerOptInAcceptedActivity;
CustomerActivityTimeline.OptedOut = CustomerOptedOutActivity;
CustomerActivityTimeline.Noted = CustomerNotedActivity;
CustomerActivityTimeline.InfluencerJoinedCampaign = InfluencerJoinedCampaignActivity;
CustomerActivityTimeline.InfluencerSubmittedPost = InfluencerSubmittedPostActivity;
CustomerActivityTimeline.InfluencerPublishedPost = InfluencerPublishedPostActivity;
CustomerActivityTimeline.Skeleton = CustomerActivityTimelineSkeleton;
CustomerActivityTimeline.Error = CustomerActivityTimelineError;
